import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import {
	Box,
	IconButton,
	ListItemIcon,
	MenuItem,
	Select,
	SelectChangeEvent,
} from "@mui/material";
import axios from "axios";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { openNavigationDrawer } from "../../../../export/drawer";
import { languageIdToPreference } from "../../../utils/language";

const secondaryColor = "#18253E";

// Autonyms are intentionally not translated.
const LANGUAGES = [
	{ id: "en", label: "English" },
	{ id: "fr", label: "Français" },
	{ id: "es", label: "Español" },
];

const LanguageSwitcher: React.FC = () => {
	const { t, i18n } = useTranslation();
	const dispatch = useDispatch();
	const isDrawerOpen = useSelector(
		(state: { navigationDrawer: { isDrawerOpen: boolean } }) =>
			state.navigationDrawer.isDrawerOpen
	);
	// Company-level gate — mirrors the mobile supervisor Settings switcher. The
	// switcher renders only when the company has multilingual support enabled.
	const multilingualEnabled = useSelector(
		(state: { user: { userData: { supervisorMultilingualEnabled?: boolean } } }) =>
			state.user?.userData?.supervisorMultilingualEnabled === true
	);

	const currentLanguage =
		LANGUAGES.find((language) => language.id === i18n.language)?.id ??
		LANGUAGES.find((language) => i18n.language?.startsWith(language.id))?.id ??
		"en";

	const handleChange = (event: SelectChangeEvent) => {
		const languageId = event.target.value;
		i18n.changeLanguage(languageId);
		localStorage.setItem("supervisor-language", languageId);
		// Persist to the supervisor's account. The change is not echoed into the
		// current session token — it surfaces on the next Authenticate/Refresh —
		// so we only fire-and-forget here (the Bearer token is added by the shared
		// axios request interceptor). Failure leaves the local selection in place.
		axios
			.put(`${process.env.REACT_APP_API_END_POINT}/Users/LanguagePreference`, {
				languagePreference: languageIdToPreference(languageId),
			})
			.catch(() => {
				/* keep the local selection; server sync retries on next change */
			});
	};

	if (!multilingualEnabled) {
		return null;
	}

	if (!isDrawerOpen) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					px: 2.5,
					height: 56,
				}}
			>
				<IconButton
					aria-label={t("drawer_language")}
					onClick={() => dispatch(openNavigationDrawer())}
					sx={{ color: secondaryColor }}
				>
					<LanguageOutlinedIcon />
				</IconButton>
			</Box>
		);
	}

	return (
		<Box sx={{ display: "flex", alignItems: "center", px: 2, height: 56 }}>
			<Select
				fullWidth
				size='small'
				value={currentLanguage}
				onChange={handleChange}
				aria-label={t("drawer_language")}
				sx={{ color: secondaryColor, fontSize: "14px" }}
				renderValue={(value) => (
					<Box sx={{ display: "flex", alignItems: "center" }}>
						<ListItemIcon sx={{ minWidth: 32, color: secondaryColor }}>
							<LanguageOutlinedIcon fontSize='small' />
						</ListItemIcon>
						{LANGUAGES.find((language) => language.id === value)?.label}
					</Box>
				)}
			>
				{LANGUAGES.map((language) => (
					<MenuItem key={language.id} value={language.id}>
						{language.label}
					</MenuItem>
				))}
			</Select>
		</Box>
	);
};

export default LanguageSwitcher;
